import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Steps, Row, Col, Card, Input, List, Typography, Empty, Spin, Grid } from 'antd';
import {
    CloudServerOutlined, ThunderboltOutlined, GlobalOutlined, ClusterOutlined, DatabaseOutlined, CodeSandboxOutlined, ContainerOutlined, RightOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { TRANSFER_TYPE_LABELS } from '../../services/transferService';
import TransferForm from '../../components/TransferForm';
import evmService from '../../../shared/services/evmService';
import cdnService from '../../services/cdnService';
import dnsService from '../../services/dnsService';
import domainRegistrationService from '../../services/domainRegistrationService';
import hostSharedService from '../../../shared/services/hostSharedService';
import hostService from '../../../shared/services/hostService';
import { containerUserApi } from '../../../shared/services/containerService';

const { Text } = Typography;

const arr = (d) => (Array.isArray(d) ? d : (d?.items || d?.list || d?.data || []));
const unwrap = (res) => (res?.success === false ? [] : arr(res?.data));

/**
 * 后端支持的类型（ResourceTransferService::TYPES）。ok = 与后端 adapter check() 一致的状态门槛，
 * 最终能否转以 preview 的 blocker 为准。
 */
export const TRANSFER_SOURCES = [
    {
        type: 'evm', icon: <CloudServerOutlined />,
        load: async () => unwrap(await evmService.getVmList({ page: 1, limit: 500 })),
        name: (r) => r.hostname || r.name || `#${r.id}`, sub: (r) => r.ip || r.ip_address,
        ok: (r) => ['active', 'flow_limit'].includes(r.status),
    },
    {
        type: 'cdn', icon: <ThunderboltOutlined />,
        load: async () => unwrap(await cdnService.getDomains()),
        name: (r) => r.domain,
        ok: (r) => !['suspended', 'deleted', 'terminated'].includes(r.status),
    },
    {
        type: 'domain', icon: <GlobalOutlined />,
        load: async () => unwrap(await domainRegistrationService.getMyDomains()),
        name: (r) => r.domain,
        ok: (r) => r.status === 'active',
    },
    {
        type: 'dns', icon: <ClusterOutlined />,
        load: async () => unwrap(await dnsService.getDnsZones()),
        name: (r) => r.display_zone || r.zone || r.name,
        ok: (r) => !['suspended', 'deleted', 'terminated'].includes(r.status),
    },
    {
        type: 'host_shared', icon: <DatabaseOutlined />,
        load: async () => unwrap(await hostSharedService.getVhosts()),
        name: (r) => r.name, sub: (r) => r.domain || r.domains?.[0]?.domain,
        ok: (r) => Number(r.status) === 1,
    },
    {
        type: 'host', icon: <CodeSandboxOutlined />,
        load: async () => unwrap(await hostService.getContainers()),
        name: (r) => r.name,
        ok: (r) => ['running', 'stopped'].includes(r.status),
    },
    {
        type: 'container', icon: <ContainerOutlined />,
        load: async () => unwrap(await containerUserApi.list()),
        name: (r) => r.label || r.name || `#${r.id}`, sub: (r) => r.image,
        ok: (r) => ['running', 'stopped'].includes(r.status) && !r.busy,
    },
];
const byType = (type) => TRANSFER_SOURCES.find((s) => s.type === type);

/** 新建转移：资源类型 → 资源 → 对方账号与确认。init = {type, id} 用于深链预填。 */
const NewTransfer = ({ open, init, onClose, onDone }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [type, setType] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [picked, setPicked] = useState(null);
    const screens = Grid.useBreakpoint();

    const src = byType(type);

    const loadType = async (tp, preId) => {
        const s = byType(tp);
        if (!s) return;
        setType(tp); setStep(1); setQ(''); setPicked(null); setItems([]); setLoading(true);
        try {
            const list = (await s.load()).filter((r) => r && r.id != null && !r.pending);
            setItems(list);
            const hit = preId != null && list.find((r) => String(r.id) === String(preId));
            if (hit && s.ok(hit)) { setPicked(hit); setStep(2); }
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        setStep(0); setType(null); setPicked(null); setItems([]); setQ('');
        if (init?.type && byType(init.type)) loadType(init.type, init.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const shown = useMemo(() => {
        if (!src) return [];
        const k = q.trim().toLowerCase();
        const list = k ? items.filter((r) => `${src.name(r) || ''} ${src.sub?.(r) || ''}`.toLowerCase().includes(k)) : items;
        return [...list].sort((a, b) => Number(src.ok(b)) - Number(src.ok(a)));
    }, [items, q, src]);

    return (
        <Modal open={open} title={t('New transfer')} onCancel={onClose} footer={null} destroyOnClose width={560}>
            <Steps size="small" current={step} responsive={false} style={{ marginBottom: 16 }}
                onChange={(s) => { if (s < step) setStep(s); }}
                items={[t('Resource type'), t('Select a resource'), t('Confirm')].map((x) => ({ title: screens.sm ? x : '' }))} />

            {step === 0 && (
                <Row gutter={[12, 12]}>
                    {TRANSFER_SOURCES.map((s) => (
                        <Col xs={12} sm={8} key={s.type}>
                            <Card hoverable size="small" onClick={() => loadType(s.type)} data-type={s.type}
                                style={{ textAlign: 'center', height: '100%' }} bodyStyle={{ padding: '16px 8px' }}>
                                <div style={{ fontSize: 24, color: 'var(--ant-color-primary, #1677ff)' }}>{s.icon}</div>
                                <div style={{ marginTop: 6 }}>{t(TRANSFER_TYPE_LABELS[s.type])}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {step === 1 && src && (
                <>
                    <Input.Search allowClear placeholder={t('Search')} value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 8 }} />
                    <Spin spinning={loading}>
                        <List
                            size="small"
                            bordered
                            style={{ maxHeight: 360, overflowY: 'auto' }}
                            dataSource={shown}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')} /> }}
                            renderItem={(r) => {
                                const ok = src.ok(r);
                                const sub = src.sub?.(r);
                                return (
                                    <List.Item
                                        data-id={r.id}
                                        onClick={ok ? () => { setPicked(r); setStep(2); } : undefined}
                                        style={{ cursor: ok ? 'pointer' : 'not-allowed', opacity: ok ? 1 : 0.5 }}
                                        extra={ok ? <RightOutlined /> : null}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{src.name(r)}</div>
                                            {(sub || !ok) && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {ok ? sub : `${t('Unavailable')} · ${r.status}`}
                                                </Text>
                                            )}
                                        </div>
                                    </List.Item>
                                );
                            }}
                        />
                    </Spin>
                </>
            )}

            {step === 2 && src && picked && (
                <TransferForm
                    key={`${type}-${picked.id}`}
                    type={type}
                    id={Number(picked.id)}
                    name={src.name(picked)}
                    onBack={() => setStep(1)}
                    onDone={onDone}
                />
            )}
        </Modal>
    );
};

export default NewTransfer;
